import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  getClientes,
  getCliente,
  crearCliente,
  actualizarCliente,
  eliminarCliente,
  getHistorial,
  getStats,
  getDashboard,
  exportarClientes,
  getPaises,
  crearPais,
  actualizarPais,
  eliminarPais,
  getCargos,
  crearCargo,
  actualizarCargo,
  eliminarCargo,
  getLineas,
  crearLinea,
  actualizarLinea,
  eliminarLinea,
  getLaboratorios,
  crearLaboratorio,
  getTiposIdentificacion,
  getTiposLaboratorio,
  getGrupos,
  crearGrupo,
  uploadExcel,
  descargarPlantilla,
  getLaboratoriosList,
  getLaboratorio,
  actualizarLaboratorioCompleto,
  eliminarLaboratorioCompleto,
  getLaboratoriosDashboard,
  getLaboratoriosStats,
  exportarLaboratorios,
  getHistorialLaboratorio,
  getTiposIdentificacionFull,
  crearTipoIdentificacion,
  actualizarTipoIdentificacion,
  getTiposLaboratorioFull,
  crearTipoLaboratorioItem,
  actualizarTipoLaboratorioItem,
} from '../api/bd-clientes.api'
import type {
  ClienteCreatePayload,
  ClienteUpdatePayload,
  FiltrosClientes,
  FiltrosDashboard,
  PaisCreatePayload,
  PaisUpdatePayload,
  CargoCreatePayload,
  CargoUpdatePayload,
  LineaCreatePayload,
  LineaUpdatePayload,
  LaboratorioCreatePayload,
  FiltrosLaboratorios,
  LaboratorioUpdatePayload,
  TipoIdentificacionCreatePayload,
  TipoIdentificacionUpdatePayload,
  TipoLaboratorioCreatePayload,
  TipoLaboratorioUpdatePayload,
} from '../types'

export const bdClientesKeys = {
  all: ['bd-clientes'] as const,
  list: (params?: FiltrosClientes) => [...bdClientesKeys.all, 'list', params] as const,
  detail: (id: number) => [...bdClientesKeys.all, 'detail', id] as const,
  stats: (params?: Record<string, unknown>) => [...bdClientesKeys.all, 'stats', params] as const,
  dashboard: (params?: FiltrosDashboard) => [...bdClientesKeys.all, 'dashboard', params] as const,
  historial: (id: number, params?: Record<string, unknown>) => [...bdClientesKeys.all, 'historial', id, params] as const,
  config: {
    all: ['bd-clientes-config'] as const,
    paises: (activo?: boolean) => ['bd-clientes-config', 'paises', activo] as const,
    cargos: (search?: string, activo?: boolean) => ['bd-clientes-config', 'cargos', search, activo] as const,
    lineas: (activo?: boolean) => ['bd-clientes-config', 'lineas', activo] as const,
    laboratorios: (paisId?: number) => ['bd-clientes-config', 'laboratorios', paisId] as const,
    tiposIdentificacion: () => ['bd-clientes-config', 'tipos-identificacion'] as const,
    tiposLaboratorio: () => ['bd-clientes-config', 'tipos-laboratorio'] as const,
    grupos: () => ['bd-clientes-config', 'grupos'] as const,
  },
}

// === QUERIES ===

export function useClientes(params?: FiltrosClientes) {
  return useQuery({
    queryKey: bdClientesKeys.list(params),
    queryFn: () => getClientes(params),
  })
}

export function useCliente(id: number) {
  return useQuery({
    queryKey: bdClientesKeys.detail(id),
    queryFn: () => getCliente(id),
    enabled: id > 0,
  })
}

export function useDashboard(params?: FiltrosDashboard) {
  return useQuery({
    queryKey: bdClientesKeys.dashboard(params),
    queryFn: () => getDashboard(params),
  })
}

export function useClienteStats(params?: Record<string, unknown>) {
  return useQuery({
    queryKey: bdClientesKeys.stats(params),
    queryFn: () => getStats(params as { pais_id?: number; laboratorio_id?: number; estado?: string }),
  })
}

export function useHistorial(id: number, params?: { page?: number; page_size?: number }) {
  return useQuery({
    queryKey: bdClientesKeys.historial(id, params),
    queryFn: () => getHistorial(id, params),
    enabled: id > 0,
  })
}

export function usePaises(activo?: boolean) {
  return useQuery({
    queryKey: bdClientesKeys.config.paises(activo),
    queryFn: () => getPaises(activo),
  })
}

export function useCargos(search?: string, activo?: boolean) {
  return useQuery({
    queryKey: bdClientesKeys.config.cargos(search, activo),
    queryFn: () => getCargos(search, activo),
  })
}

export function useLineas(activo?: boolean) {
  return useQuery({
    queryKey: bdClientesKeys.config.lineas(activo),
    queryFn: () => getLineas(activo),
  })
}

export function useLaboratorios(paisId?: number) {
  return useQuery({
    queryKey: bdClientesKeys.config.laboratorios(paisId),
    queryFn: () => getLaboratorios(paisId),
    enabled: paisId !== undefined && paisId > 0,
  })
}

export function useTiposIdentificacion() {
  return useQuery({
    queryKey: bdClientesKeys.config.tiposIdentificacion(),
    queryFn: () => getTiposIdentificacion(),
  })
}

export function useTiposLaboratorio() {
  return useQuery({
    queryKey: bdClientesKeys.config.tiposLaboratorio(),
    queryFn: () => getTiposLaboratorio(),
  })
}

export function useGrupos() {
  return useQuery({
    queryKey: bdClientesKeys.config.grupos(),
    queryFn: () => getGrupos(),
  })
}

export function useCrearGrupo() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { name: string }) => crearGrupo(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bdClientesKeys.config.all })
      toast.success('Grupo creado correctamente')
    },
    onError: () => toast.error('Error al crear grupo'),
  })
}

export function useCrearLaboratorio() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: LaboratorioCreatePayload) => crearLaboratorio(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bdClientesKeys.config.all })
      toast.success('Laboratorio creado correctamente')
    },
    onError: () => toast.error('Error al crear laboratorio'),
  })
}

// === MUTATIONS ===

export function useCrearCliente() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: ClienteCreatePayload) => crearCliente(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bdClientesKeys.all })
      toast.success('Cliente creado correctamente')
    },
    onError: () => toast.error('Error al crear cliente'),
  })
}

export function useActualizarCliente() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: ClienteUpdatePayload }) =>
      actualizarCliente(id, payload),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: bdClientesKeys.all })
      queryClient.invalidateQueries({ queryKey: bdClientesKeys.detail(id) })
      toast.success('Cliente actualizado correctamente')
    },
    onError: () => toast.error('Error al actualizar cliente'),
  })
}

export function useEliminarCliente() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => eliminarCliente(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bdClientesKeys.all })
      toast.success('Cliente eliminado correctamente')
    },
    onError: () => toast.error('Error al eliminar cliente'),
  })
}

export function useExportarClientes() {
  return useMutation({
    mutationFn: (params?: FiltrosClientes) => exportarClientes(params),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `bd_clientes_${new Date().toISOString().slice(0, 10)}.xlsx`
      a.click()
      URL.revokeObjectURL(url)
      toast.success('Archivo descargado')
    },
    onError: () => toast.error('Error al exportar'),
  })
}

export function useUploadExcel() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (file: File) => uploadExcel(file),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: bdClientesKeys.all })
      if (result.errores_count === 0) {
        toast.success(`${result.exitosos} clientes cargados correctamente`)
      } else {
        toast.warning(`${result.exitosos} exitosos, ${result.errores_count} errores`)
      }
    },
    onError: () => toast.error('Error al procesar el archivo'),
  })
}

export function useDescargarPlantilla() {
  return useMutation({
    mutationFn: () => descargarPlantilla(),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'plantilla_clientes.xlsx'
      a.click()
      URL.revokeObjectURL(url)
    },
    onError: () => toast.error('Error al descargar plantilla'),
  })
}

// === CONFIG MUTATIONS ===

export function useCrearPais() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: PaisCreatePayload) => crearPais(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bdClientesKeys.config.all })
      toast.success('País creado correctamente')
    },
    onError: () => toast.error('Error al crear país'),
  })
}

export function useActualizarPais() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: PaisUpdatePayload }) =>
      actualizarPais(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bdClientesKeys.config.all })
      toast.success('País actualizado correctamente')
    },
    onError: () => toast.error('Error al actualizar país'),
  })
}

export function useEliminarPais() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => eliminarPais(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bdClientesKeys.config.all })
      toast.success('País eliminado correctamente')
    },
    onError: () => toast.error('No se puede eliminar: tiene clientes o laboratorios asociados'),
  })
}

export function useCrearCargo() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CargoCreatePayload) => crearCargo(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bdClientesKeys.config.all })
      toast.success('Cargo creado correctamente')
    },
    onError: () => toast.error('Error al crear cargo'),
  })
}

export function useActualizarCargo() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: CargoUpdatePayload }) =>
      actualizarCargo(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bdClientesKeys.config.all })
      toast.success('Cargo actualizado correctamente')
    },
    onError: () => toast.error('Error al actualizar cargo'),
  })
}

export function useEliminarCargo() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => eliminarCargo(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bdClientesKeys.config.all })
      toast.success('Cargo eliminado correctamente')
    },
    onError: () => toast.error('No se puede eliminar: tiene clientes asociados'),
  })
}

export function useCrearLinea() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: LineaCreatePayload) => crearLinea(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bdClientesKeys.config.all })
      toast.success('Línea creada correctamente')
    },
    onError: () => toast.error('Error al crear línea'),
  })
}

export function useActualizarLinea() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: LineaUpdatePayload }) =>
      actualizarLinea(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bdClientesKeys.config.all })
      toast.success('Línea actualizada correctamente')
    },
    onError: () => toast.error('Error al actualizar línea'),
  })
}

export function useEliminarLinea() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => eliminarLinea(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bdClientesKeys.config.all })
      toast.success('Línea eliminada correctamente')
    },
    onError: () => toast.error('No se puede eliminar: tiene clientes asociados'),
  })
}

// ============================================================================
// LABORATORIOS
// ============================================================================

export const laboratoriosKeys = {
  all: ['laboratorios'] as const,
  list: (params?: FiltrosLaboratorios) => [...laboratoriosKeys.all, 'list', params] as const,
  detail: (id: number) => [...laboratoriosKeys.all, 'detail', id] as const,
  stats: (params?: Record<string, unknown>) => [...laboratoriosKeys.all, 'stats', params] as const,
  dashboard: (params?: FiltrosLaboratorios) => [...laboratoriosKeys.all, 'dashboard', params] as const,
  historial: (id: number, params?: Record<string, unknown>) => [...laboratoriosKeys.all, 'historial', id, params] as const,
  config: {
    all: ['laboratorios-config'] as const,
    tiposIdentificacion: (activo?: boolean) => ['laboratorios-config', 'tipos-identificacion', activo] as const,
    tiposLaboratorio: (activo?: boolean) => ['laboratorios-config', 'tipos-laboratorio', activo] as const,
  },
}

// === LABORATORIOS QUERIES ===

export function useLaboratoriosList(params?: FiltrosLaboratorios) {
  return useQuery({
    queryKey: laboratoriosKeys.list(params),
    queryFn: () => getLaboratoriosList(params),
  })
}

export function useLaboratorio(id: number) {
  return useQuery({
    queryKey: laboratoriosKeys.detail(id),
    queryFn: () => getLaboratorio(id),
    enabled: id > 0,
  })
}

export function useLaboratoriosDashboard(params?: FiltrosLaboratorios) {
  return useQuery({
    queryKey: laboratoriosKeys.dashboard(params),
    queryFn: () => getLaboratoriosDashboard(params),
  })
}

export function useLaboratoriosStats(params?: Record<string, unknown>) {
  return useQuery({
    queryKey: laboratoriosKeys.stats(params),
    queryFn: () => getLaboratoriosStats(params),
  })
}

export function useHistorialLaboratorio(id: number, params?: { page?: number; page_size?: number }) {
  return useQuery({
    queryKey: laboratoriosKeys.historial(id, params),
    queryFn: () => getHistorialLaboratorio(id, params),
    enabled: id > 0,
  })
}

export function useTiposIdentificacionFull(activo?: boolean) {
  return useQuery({
    queryKey: laboratoriosKeys.config.tiposIdentificacion(activo),
    queryFn: () => getTiposIdentificacionFull(activo),
  })
}

export function useTiposLaboratorioFull(activo?: boolean) {
  return useQuery({
    queryKey: laboratoriosKeys.config.tiposLaboratorio(activo),
    queryFn: () => getTiposLaboratorioFull(activo),
  })
}

// === LABORATORIOS MUTATIONS ===

export function useCrearLaboratorioCompleto() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: LaboratorioCreatePayload) => crearLaboratorio(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: laboratoriosKeys.all })
      queryClient.invalidateQueries({ queryKey: bdClientesKeys.config.all })
      toast.success('Laboratorio creado correctamente')
    },
    onError: () => toast.error('Error al crear laboratorio'),
  })
}

export function useActualizarLaboratorioCompleto() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: LaboratorioUpdatePayload }) =>
      actualizarLaboratorioCompleto(id, payload),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: laboratoriosKeys.all })
      queryClient.invalidateQueries({ queryKey: laboratoriosKeys.detail(id) })
      toast.success('Laboratorio actualizado correctamente')
    },
    onError: () => toast.error('Error al actualizar laboratorio'),
  })
}

export function useEliminarLaboratorioCompleto() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => eliminarLaboratorioCompleto(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: laboratoriosKeys.all })
      toast.success('Laboratorio eliminado correctamente')
    },
    onError: () => toast.error('Error al eliminar laboratorio'),
  })
}

export function useExportarLaboratorios() {
  return useMutation({
    mutationFn: (params?: FiltrosLaboratorios) => exportarLaboratorios(params),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `laboratorios_${new Date().toISOString().slice(0, 10)}.xlsx`
      a.click()
      URL.revokeObjectURL(url)
      toast.success('Archivo descargado')
    },
    onError: () => toast.error('Error al exportar'),
  })
}

export function useCrearTipoIdentificacion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: TipoIdentificacionCreatePayload) => crearTipoIdentificacion(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: laboratoriosKeys.config.all })
      toast.success('Tipo de identificación creado correctamente')
    },
    onError: () => toast.error('Error al crear tipo de identificación'),
  })
}

export function useActualizarTipoIdentificacion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: TipoIdentificacionUpdatePayload }) =>
      actualizarTipoIdentificacion(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: laboratoriosKeys.config.all })
      toast.success('Tipo de identificación actualizado correctamente')
    },
    onError: () => toast.error('Error al actualizar tipo de identificación'),
  })
}

export function useCrearTipoLaboratorioItem() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: TipoLaboratorioCreatePayload) => crearTipoLaboratorioItem(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: laboratoriosKeys.config.all })
      toast.success('Tipo de laboratorio creado correctamente')
    },
    onError: () => toast.error('Error al crear tipo de laboratorio'),
  })
}

export function useActualizarTipoLaboratorioItem() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: TipoLaboratorioUpdatePayload }) =>
      actualizarTipoLaboratorioItem(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: laboratoriosKeys.config.all })
      toast.success('Tipo de laboratorio actualizado correctamente')
    },
    onError: () => toast.error('Error al actualizar tipo de laboratorio'),
  })
}
